import type { PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from 'react'
import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { IconButton } from './IconButton'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  description?: string
  size?: 'sm' | 'md' | 'lg'
  footer?: ReactNode
  children: ReactNode
}

const sizes: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-xl',
  lg: 'sm:max-w-3xl'
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.offsetWidth > 0 || element.offsetHeight > 0
  )
}

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  children
}: ModalProps): ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const pressStartedInside = useRef(false)
  const generatedId = useId()
  const titleId = `modal-${generatedId}-title`
  const descriptionId = `modal-${generatedId}-description`

  // Held in a ref so an inline `onClose` arrow can't re-run the setup effect —
  // that would re-lock scroll and bounce focus on every parent render.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Captured on the render that opens the dialog, not in the effect: React applies
  // a child's `autoFocus` during commit, so by effect time `activeElement` would
  // already be inside the panel and the real trigger would be lost.
  const restoreRef = useRef<HTMLElement | null>(null)
  const previousOpen = useRef(false)
  if (open && !previousOpen.current) {
    restoreRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
  }
  previousOpen.current = open

  useEffect(() => {
    if (!open) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => {
      const panel = panelRef.current
      if (!panel) return
      if (panel.contains(document.activeElement)) return // respect an autoFocus'd field
      const preferred = panel.querySelector<HTMLElement>('[data-autofocus]')
      ;(preferred ?? panel).focus()
    }, 0)

    // Bubble phase, so an open popover inside the dialog can swallow Escape first.
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      const items = focusableWithin(panel)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        event.preventDefault()
        panel?.focus()
        return
      }
      const active = document.activeElement
      const inside = panel?.contains(active) === true
      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      restoreRef.current?.focus()
    }
  }, [open])

  if (!open) return null

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    pressStartedInside.current = panelRef.current?.contains(event.target as Node) === true
  }

  return createPortal(
    <div
      ref={overlayRef}
      onPointerDown={onPointerDown}
      onClick={(event) => {
        // Ignore a click whose press began inside the panel (e.g. a text drag).
        if (pressStartedInside.current) return
        if (event.target === overlayRef.current) onCloseRef.current()
      }}
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-ink-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-6 dark:bg-ink-950/70"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'card animate-rise relative flex max-h-[92dvh] w-full flex-col rounded-b-none shadow-pop outline-none sm:rounded-card',
          sizes[size]
        )}
      >
        {title || description ? (
          <div className="flex items-start gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0 flex-1">
              {title ? (
                <h2 id={titleId} className="text-base font-semibold leading-tight text-fg">
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p id={descriptionId} className="mt-1 text-sm leading-relaxed text-fg-muted">
                  {description}
                </p>
              ) : null}
            </div>
            <IconButton label="Close dialog" size="sm" onClick={() => onCloseRef.current()}>
              <X />
            </IconButton>
          </div>
        ) : (
          <div className="absolute right-3 top-3 z-10">
            <IconButton label="Close dialog" size="sm" onClick={() => onCloseRef.current()}>
              <X />
            </IconButton>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line bg-sunken/40 px-5 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  )
}
